import React from 'react';
import { Header } from '../components/Header';
import { Card } from '../components/Card';
import { Badge } from '../components/Badge';
import { ArrowLeftIcon, TrendingUpIcon, ClockIcon, CheckCircleIcon, AlertTriangleIcon, FlameIcon, AlertOctagonIcon, HelpCircleIcon, MessageSquareIcon, ShieldIcon, DownloadIcon } from 'lucide-react';
interface AdminScreenProps {
  onBack: () => void;
  onLogout: () => void;
}
export function AdminScreen({
  onBack,
  onLogout
}: AdminScreenProps) {
  // Mock data for the admin dashboard
  const progress = {
    total: 2847,
    reviewed: 892,
    burnRate: 145,
    estCompletion: 'Oct 28, 2023'
  };
  const teamActivity = [{
    name: 'Sarah Chen',
    role: 'Lead Reviewer',
    reviewed: 412,
    hot: 28,
    critical: 64,
    tbd: 12,
    avgTime: '42s',
    lastActive: 'Just now',
    status: 'active'
  }, {
    name: 'Michael Torres',
    role: 'Reviewer',
    reviewed: 308,
    hot: 14,
    critical: 42,
    tbd: 22,
    avgTime: '58s',
    lastActive: '2 hrs ago',
    status: 'active'
  }, {
    name: 'Jessica Wong',
    role: 'Reviewer',
    reviewed: 172,
    hot: 5,
    critical: 18,
    tbd: 4,
    avgTime: '1m 15s',
    lastActive: 'Yesterday',
    status: 'warning' // Inactive > 24h
  }];
  const qualitySignals = {
    avgTbdRate: '4.2%',
    totalComments: 156,
    flaggedForQa: 24,
    privileged: 89
  };
  const pctComplete = Math.round(progress.reviewed / progress.total * 100);
  return <div className="min-h-screen flex flex-col bg-navy" data-id="element-430">
      <Header caseName="Smith v. Acme Corp" onLogout={onLogout} data-id="element-431" />

      <main className="flex-1 overflow-y-auto p-8" data-id="element-432">
        <div className="max-w-6xl mx-auto space-y-8" data-id="element-433">
          {/* Navigation & Header */}
          <div data-id="element-434">
            <button onClick={onBack} className="flex items-center text-sm text-gray-400 hover:text-white mb-6 transition-colors" data-id="element-435">
              <ArrowLeftIcon className="w-4 h-4 mr-2" data-id="element-436" />
              Back to Case Overview
            </button>

            <div className="flex items-start justify-between" data-id="element-437">
              <div data-id="element-438">
                <h1 className="text-3xl font-serif text-white mb-2" data-id="element-439">
                  Admin Dashboard
                </h1>
                <p className="text-gray-400" data-id="element-440">
                  Team performance, review progress, and quality oversight.
                </p>
              </div>
              <button className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-300 bg-navy border border-navy-lighter rounded-lg hover:bg-navy-hover hover:text-white transition-colors" data-id="element-441">
                <DownloadIcon className="w-4 h-4" data-id="element-442" />
                Export Audit Log
              </button>
            </div>
          </div>

          {/* 1. Review Progress */}
          <Card className="p-6" data-id="element-443">
            <h2 className="text-lg font-serif text-white mb-6" data-id="element-444">
              Review Progress
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8" data-id="element-445">
              <div data-id="element-446">
                <div className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-1" data-id="element-447">
                  Total Documents
                </div>
                <div className="text-3xl font-serif text-white" data-id="element-448">
                  {progress.total.toLocaleString()}
                </div>
              </div>
              <div data-id="element-449">
                <div className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-1" data-id="element-450">
                  Reviewed
                </div>
                <div className="text-3xl font-serif text-green-400" data-id="element-451">
                  {progress.reviewed.toLocaleString()}
                </div>
              </div>
              <div data-id="element-452">
                <div className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-1" data-id="element-453">
                  Burn Rate
                </div>
                <div className="flex items-baseline gap-2" data-id="element-454">
                  <div className="text-3xl font-serif text-white" data-id="element-455">
                    {progress.burnRate}
                  </div>
                  <div className="text-sm text-gray-500" data-id="element-456">docs/day</div>
                </div>
              </div>
              <div data-id="element-457">
                <div className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-1" data-id="element-458">
                  Est. Completion
                </div>
                <div className="text-3xl font-serif text-amber" data-id="element-459">
                  {progress.estCompletion}
                </div>
              </div>
            </div>

            {/* Progress Bar */}
            <div data-id="element-460">
              <div className="flex justify-between text-sm mb-2" data-id="element-461">
                <span className="text-gray-400" data-id="element-462">{pctComplete}% Complete</span>
                <span className="text-gray-500" data-id="element-463">
                  {(progress.total - progress.reviewed).toLocaleString()}{' '}
                  remaining
                </span>
              </div>
              <div className="h-3 w-full bg-navy-lighter rounded-full overflow-hidden" data-id="element-464">
                <div className="h-full bg-green-500 rounded-full transition-all duration-1000" style={{
                width: `${pctComplete}%`
              }} data-id="element-465" />
              </div>
            </div>
          </Card>

          {/* 2. Team Activity */}
          <Card className="overflow-hidden" data-id="element-466">
            <div className="p-6 border-b border-navy-lighter flex justify-between items-center" data-id="element-467">
              <h2 className="text-lg font-serif text-white" data-id="element-468">Team Activity</h2>
              <Badge variant="blue" data-id="element-469">
                {teamActivity.length} Active Reviewers
              </Badge>
            </div>

            <div className="overflow-x-auto" data-id="element-470">
              <table className="w-full text-left border-collapse" data-id="element-471">
                <thead data-id="element-472">
                  <tr className="bg-navy-lighter/30 text-xs uppercase tracking-wider text-gray-400 border-b border-navy-lighter" data-id="element-473">
                    <th className="px-6 py-4 font-semibold" data-id="element-474">Reviewer</th>
                    <th className="px-6 py-4 font-semibold text-right" data-id="element-475">
                      Reviewed
                    </th>
                    <th className="px-6 py-4 font-semibold text-center" data-id="element-476">
                      Classifications
                    </th>
                    <th className="px-6 py-4 font-semibold text-right" data-id="element-477">
                      Avg Time
                    </th>
                    <th className="px-6 py-4 font-semibold" data-id="element-478">Last Active</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-navy-lighter" data-id="element-479">
                  {teamActivity.map((member, i) => <tr key={i} className="hover:bg-navy-hover/50 transition-colors" data-id="element-480">
                      <td className="px-6 py-4" data-id="element-481">
                        <div className="flex items-center gap-3" data-id="element-482">
                          <div className={`w-2 h-2 rounded-full ${member.status === 'active' ? 'bg-green-500' : 'bg-amber'}`} data-id="element-483" />
                          <div data-id="element-484">
                            <div className="text-white font-medium" data-id="element-485">
                              {member.name}
                            </div>
                            <div className="text-xs text-gray-500" data-id="element-486">
                              {member.role}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right" data-id="element-487">
                        <span className="text-lg font-serif text-white" data-id="element-488">
                          {member.reviewed}
                        </span>
                      </td>
                      <td className="px-6 py-4" data-id="element-489">
                        <div className="flex items-center justify-center gap-3 text-xs" data-id="element-490">
                          <div className="flex items-center gap-1 text-red-400" title="Hot" data-id="element-491">
                            <FlameIcon className="w-3.5 h-3.5" data-id="element-492" />
                            {member.hot}
                          </div>
                          <div className="flex items-center gap-1 text-orange-400" title="Critical" data-id="element-493">
                            <AlertOctagonIcon className="w-3.5 h-3.5" data-id="element-494" />
                            {member.critical}
                          </div>
                          <div className="flex items-center gap-1 text-yellow-400" title="TBD" data-id="element-495">
                            <HelpCircleIcon className="w-3.5 h-3.5" data-id="element-496" />
                            {member.tbd}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right text-gray-300" data-id="element-497">
                        {member.avgTime}
                      </td>
                      <td className="px-6 py-4" data-id="element-498">
                        <span className={`text-sm ${member.status === 'warning' ? 'text-amber' : 'text-gray-400'}`} data-id="element-499">
                          {member.lastActive}
                        </span>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </Card>

          {/* 3. Quality Signals */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-500">
            <Card className="p-6 flex flex-col items-center text-center justify-center" data-id="element-501">
              <div className="w-12 h-12 rounded-full bg-amber/10 flex items-center justify-center mb-4 text-amber" data-id="element-502">
                <HelpCircleIcon className="w-6 h-6" data-id="element-503" />
              </div>
              <div className="text-3xl font-serif text-white mb-1" data-id="element-504">
                {qualitySignals.avgTbdRate}
              </div>
              <div className="text-sm font-medium text-gray-400 uppercase tracking-wider" data-id="element-505">
                Avg TBD Rate
              </div>
              <p className="text-xs text-gray-500 mt-2" data-id="element-506">
                Team average. High rates may indicate unclear review guidelines.
              </p>
            </Card>

            <Card className="p-6 flex flex-col items-center text-center justify-center" data-id="element-507">
              <div className="w-12 h-12 rounded-full bg-blue-500/10 flex items-center justify-center mb-4 text-blue-400" data-id="element-508">
                <MessageSquareIcon className="w-6 h-6" data-id="element-509" />
              </div>
              <div className="text-3xl font-serif text-white mb-1" data-id="element-510">
                {qualitySignals.totalComments}
              </div>
              <div className="text-sm font-medium text-gray-400 uppercase tracking-wider" data-id="element-511">
                Total Comments
              </div>
              <p className="text-xs text-gray-500 mt-2" data-id="element-512">
                Notes left by reviewers on documents.
              </p>
            </Card>

            <Card className="p-6 flex flex-col items-center text-center justify-center border-amber/30" data-id="element-513">
              <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center mb-4 text-red-400" data-id="element-514">
                <ShieldIcon className="w-6 h-6" data-id="element-515" />
              </div>
              <div className="text-3xl font-serif text-white mb-1" data-id="element-516">
                {qualitySignals.privileged}
              </div>
              <div className="text-sm font-medium text-gray-400 uppercase tracking-wider" data-id="element-517">
                Privileged Docs
              </div>
              <p className="text-xs text-gray-500 mt-2" data-id="element-518">
                Documents tagged as privileged requiring partner review.
              </p>
            </Card>
          </div>
        </div>
      </main>
    </div>;
}